
import React, { useEffect, useState } from "react";
import { Carousel } from "primereact/carousel";
import { Paginator } from "primereact/paginator"; // Import the Paginator
import { DetailBox } from "../../molecules/DetailBox";
import { QuantamVideo } from "container/QuantamVideo";
import styled from "styled-components";

/**
 * Mirrors PrimeReact Carousel breakpoint logic so Paginator `rows` always matches
 * the carousel's effective numVisible. Otherwise zoom / narrower viewport switches the
 * carousel to fewer columns while Paginator still uses rows=4 → last slides disappear
 * with no second page.
 * numScroll === numVisible so each paginator page equals one carousel page.
 */
const presenceGalleryResponsiveOptions = [
  { breakpoint: "1600px", numVisible: 4, numScroll: 4 },
  { breakpoint: "1400px", numVisible: 3, numScroll: 3 },
  { breakpoint: "1199px", numVisible: 3, numScroll: 3 },
  { breakpoint: "768px", numVisible: 1, numScroll: 1 },
  { breakpoint: "575px", numVisible: 1, numScroll: 1 },
];

const FALLBACK_WIDE_COLUMNS = 4;

function matchPresenceGalleryRows(windowWidth, responsiveOptions, fallbackRows) {
  const sorted = [...responsiveOptions].sort(
    (a, b) =>
      parseInt(String(b.breakpoint), 10) - parseInt(String(a.breakpoint), 10)
  );
  let matched = { numVisible: fallbackRows, numScroll: fallbackRows };
  for (let i = 0; i < sorted.length; i++) {
    const res = sorted[i];
    if (parseInt(String(res.breakpoint), 10) >= windowWidth) {
      matched = res;
    }
  }
  return matched.numVisible;
}

// Custom Styled Paginator
const CustPaginator = styled(Paginator)`
  .p-paginator-first:not(.p-disabled):not(.p-highlight):hover,
  .p-paginator-prev:not(.p-disabled):not(.p-highlight):hover,
  .p-paginator-next:not(.p-disabled):not(.p-highlight):hover,
  .p-paginator-last:not(.p-disabled):not(.p-highlight):hover {
    color: #000;
  }

  .p-paginator-pages {
    margin: 0 1rem;
  }
  .p-paginator-pages .p-paginator-page.p-highlight {
    background: #010101;
    border-color: #010101;
    color: #fff;
    cursor: default;
  }

  .p-paginator-pages .p-paginator-page {
    border: none;
    border-radius: 50% !important;
    color: #010101;
    float: left;
    font-size: 18px;
    font-weight: 500;
    height: 2pc;
    line-height: 2pc;
    min-width: 2pc;
    padding: 0 !important;
    text-align: center;
    text-decoration: none;
  }

  .p-paginator-pages .p-paginator-page:hover {
    background: #010101;
    color: #fff;
    text-decoration: none;
  }
`;

export const PresenceGallery = (props) => {
  const data = props.data ?? [];

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(() =>
    typeof window !== "undefined"
      ? matchPresenceGalleryRows(
          window.innerWidth,
          presenceGalleryResponsiveOptions,
          FALLBACK_WIDE_COLUMNS
        )
      : FALLBACK_WIDE_COLUMNS
  );
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    const syncRowsFromViewport = () => {
      const w =
        typeof window !== "undefined" ? window.innerWidth : 1600;
      const next = matchPresenceGalleryRows(
        w,
        presenceGalleryResponsiveOptions,
        FALLBACK_WIDE_COLUMNS
      );
      setRows((prev) => (prev !== next ? next : prev));
    };

    syncRowsFromViewport();
    window.addEventListener("resize", syncRowsFromViewport);
    window.visualViewport?.addEventListener("resize", syncRowsFromViewport);

    return () => {
      window.removeEventListener("resize", syncRowsFromViewport);
      window.visualViewport?.removeEventListener(
        "resize",
        syncRowsFromViewport
      );
    };
  }, []);

  useEffect(() => {
    const len = data.length;
    if (!len) {
      setFirst(0);
      setActivePage(0);
      return;
    }
    const totalPages = Math.max(1, Math.ceil(len / rows));
    setActivePage((prev) => {
      const clamped = Math.min(Math.max(0, prev), totalPages - 1);
      setFirst(clamped * rows);
      return clamped;
    });
  }, [rows, data.length]);

  const productTemplate = (item, index) => {
    return (
      <div
        id={item.id != null ? `presence-${item.id}` : undefined}
        className=" p-0  insightDiv responsiveDiv d-flex align-items-start flex-column  "
      >
        {item.image ? (
          <div className="image-containerMap ">
            <img
              src={item.image}
              className="w-full zoom-image"
              style={{ width: "100%", height: "100%" }}
              alt=""
            />
          </div>
        ) : (
          <QuantamVideo item={item} />
        )}
        <h2
          className={`${props.titlecss} mt-3 underlineText  fontAva font19 `}
          style={{ fontSize: "1.5rem", textAlign: "center !important" }}
        >
          {item.title}
        </h2>
        <DetailBox details={item.description} className={`${props.detailcss}`} />
      </div>
    );
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setActivePage(event.page);
  };

  return (
    <div className="container  p-0 ">
      <Carousel
        key={`${data.length}-${rows}`}
        value={data}
        page={activePage}
        numVisible={rows}
        numScroll={rows}
        responsiveOptions={presenceGalleryResponsiveOptions}
        itemTemplate={productTemplate}
        showIndicators={false}
        showNavigators={false}
        circular={true}
        style={{ backgroundColor: "transparent" }}
      />

      <div className="container flex justify-content-end flex-wrap mb-0 pr-0">
        <CustPaginator
          first={first}
          rows={rows}
          totalRecords={data.length}
          onPageChange={onPageChange}
          prevPageLinkIcon={
            <i className="pi pi-arrow-circle-left" style={{ fontSize: "2rem" }} />
          }
          nextPageLinkIcon={
            <i className="pi pi-arrow-circle-right" style={{ fontSize: "2rem" }} />
          }
          currentPageReportTemplate="{currentPage} of {totalPages}" // Custom report template without parentheses
          template="PrevPageLink CurrentPageReport NextPageLink" // Template layout
        />
      </div>
    </div>
  );
};
